/* eslint-disable react/jsx-key */
import React, {useState} from 'react';
import moment from 'moment';
import {
  string,
  shape,
  arrayOf,
  bool,
  func,
} from 'prop-types';
import classNames from 'classnames';
import { toKebabCase } from 'helpers/Utils';
import {
  Heading,
  Label,
  CountDown,
} from 'components/common';
import ProductImage from 'components/product/ProductImage';

import './Header.scss';

export const staticTextKeys = [
  'RESULTS_PAGE_PROMOTIONAL_LABEL_LOADING_TEXT',
  'RESULTS_PAGE_PROMOTIONAL_LABEL_END_TEXT',
];

const HeaderLabel = ({ type, name }) => (
  <Label type={type} inlineSpacing={true}>
    {name}
  </Label>
);

HeaderLabel.propTypes = {
  type: string.isRequired,
  name: string.isRequired,
};

const HeaderLabels = ({
  labels,
}) => {
  const [countdownFinish, setCountdownFinish] = useState(false);

  return (
    <span className="labels">
      {labels.map(({ key, display, countdownFormat }, index) => {
        const newKey = toKebabCase(key);
        if (key !== 'TIME_LIMITED_COUNT') {
          return (
            <HeaderLabel
              // eslint-disable-next-line react/no-array-index-key
              key={newKey + index}
              type={newKey}
              name={display}
            />
          );
        }
        if (!countdownFormat?.date?.endDate || !moment(countdownFormat?.date?.endDate).isValid() || countdownFinish) {
          return null;
        } else if (countdownFormat?.date?.endDate && moment(countdownFormat?.date?.endDate).isValid()) {
          const currentTime = moment().utcOffset(countdownFormat?.timeOffset).unix();
          const eventTime = moment(countdownFormat?.date?.endDate).utcOffset(countdownFormat?.timeOffset).unix();
          const diffTime = eventTime - currentTime;
          if (diffTime <= 0) return null;
        }

        return (
          <Label timeLimitedCount inlineSpacing type={newKey}>
            <CountDown
              // eslint-disable-next-line react/no-array-index-key
              key={newKey + index}
              type={newKey}
              name={display}
              toTime={countdownFormat?.date?.endDate}
              timeOffset={countdownFormat?.timeOffset}
              loadingText={countdownFormat?.loadingText}
              endText={countdownFormat?.endText}
              displayFormat={countdownFormat?.displayFormat || ''}
              maxUnit={countdownFormat?.maxUnit}
              variant="secondary"
              weight="semiBold"
              onEnded={() => {
                if (!countdownFinish)
                  setCountdownFinish(true);
              }}
            />
          </Label>
        );
      })}
    </span>
  )
}

HeaderLabels.propTypes = {
  labels: arrayOf(
    shape({
      id: string,
      name: string,
    })
  ),
};

HeaderLabels.defaultProps = {
  labels: [],
};

const Header = (props) => {
  const {
    name,
    image = {},
    labels = {},
    titleRoute,
    imageRoute,
    imageOrientation,
    list,
    lazyLoadImg,
    onImageClick,
    ssHeading,
  } = props;
  const productPageLink = (!titleRoute && imageRoute)
    ? encodeURI(`${imageRoute}?entry=image${list ? `&from=${list}` : ''}`)
    : '';
  return (
    <div className="header" data-selector-type="header">
      <ProductImage
        orientation={imageOrientation}
        imageDimensions={{ height: 36 }}
        alt={name}
        src={image?.url}
        to={productPageLink}
        className={classNames('image', {
          'image-portrait': imageOrientation === 'PORTRAIT',
        })}
        onImageClick={onImageClick}
        lazy={lazyLoadImg}
      />
      <div className="title-wrapper">
        <div className="title" data-selector-type="header-title">
          {ssHeading ? (
            <h2 className="heading h3">
              {name}
            </h2>
          ) : (
            <Heading as="h3">
              {name}
            </Heading>
          )}
        </div>
        {labels && <HeaderLabels labels={labels} />}
      </div>
    </div>
  );
};

Header.propTypes = {
  name: string.isRequired,
  image: shape({
    url: string.isRequired,
    alt: string,
  }),
  labels: arrayOf(
    shape({
      id: string,
      name: string,
    })
  ),
  titleRoute: string,
  imageRoute: string,
  list: string,
  imageOrientation: string,
  lazyLoadImg: bool,
  onImageClick: func,
  ssHeading: bool,
};

Header.defaultProps = {
  image: null,
  labels: [],
  titleRoute: null,
  imageRoute: null,
  list: null,
  imageOrientation: undefined,
  lazyLoadImg: true,
  onImageClick: undefined,
  ssHeading: false,
};

export default Header;
